/**
 * Account Manager Layout
 * 
 * Layout wrapper for AM portal pages.
 * Navy/gold/cream brand palette with gold accent on active nav.
 */

import { ReactNode, useState } from "react";
import { NavLink } from "@/components/NavLink";
import { useLocation, Link, useNavigate } from "react-router-dom";
import {
  Users,
  MessageSquare,
  CheckSquare,
  Settings,
  Briefcase,
  LogOut,
  LayoutDashboard,
  Menu,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAdmin } from "@/hooks/useAdmin";
import { useAuth } from "@/hooks/useAuth";
import { useAccountManager } from "@/hooks/useAccountManager";
import { useIsMobile } from "@/hooks/use-mobile";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import AMNotificationBell from "@/components/am/AMNotificationBell";

interface AMLayoutProps {
  children: ReactNode;
}

interface NavItem {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
  disabled?: boolean;
}

const navItems: NavItem[] = [
  { title: "My Clients", url: "/am", icon: Users },
  { title: "Inbox", url: "/am/inbox", icon: MessageSquare },
  { title: "Tasks", url: "/am/tasks", icon: CheckSquare },
  { title: "Settings", url: "/am/settings", icon: Settings },
];

export function AMLayout({ children }: AMLayoutProps) {
  const location = useLocation();
  const { role } = useAdmin();
  const { signOut } = useAuth();
  const { accountManager } = useAccountManager();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navContent = (
    <nav className="flex flex-col gap-1 p-4">
      {navItems.map((item) => {
        const isActive = location.pathname === item.url;
        if (item.disabled) {
          return (
            <div
              key={item.url}
              className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground/50 cursor-not-allowed"
            >
              <item.icon className="h-4 w-4" />
              {item.title}
              <span className="ml-auto text-[10px] bg-muted px-1.5 py-0.5 rounded">Soon</span>
            </div>
          );
        }
        return (
          <NavLink
            key={item.url}
            to={item.url}
            end
            onClick={() => isMobile && setSidebarOpen(false)}
            className={cn(
              "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
              "hover:bg-[#D4A853]/10 hover:text-foreground",
              isActive
                ? "bg-[#D4A853]/15 text-[#D4A853] border-l-2 border-[#D4A853]"
                : "text-muted-foreground"
            )}
          >
            <item.icon className="h-4 w-4" />
            {item.title}
          </NavLink>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Top Bar */}
      <header className="sticky top-0 z-50 w-full border-b bg-card">
        <div className={`flex h-14 items-center ${isMobile ? "px-3" : "px-6"}`}>
          <div className="flex items-center gap-3">
            {isMobile && (
              <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)} className="mr-1">
                <Menu className="h-5 w-5" />
              </Button>
            )}
            <Briefcase className="h-5 w-5 text-[#D4A853]" />
            <span className={`font-semibold text-foreground ${isMobile ? "text-sm" : ""}`}>
              {isMobile ? "Account Manager" : "Trusted James — Account Manager"}
            </span>
          </div>
          <div className="ml-auto flex items-center gap-2 md:gap-4">
            <AMNotificationBell amId={accountManager?.id || null} />
            {role === "admin" && (
              <Link
                to="/admin"
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
              >
                <LayoutDashboard className="h-3 w-3" />
                {!isMobile && "Admin"}
              </Link>
            )}
            <button
              onClick={async () => {
                await signOut();
                navigate("/login", { replace: true });
              }}
              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
            >
              <LogOut className="h-3 w-3" />
              {!isMobile && "Sign Out"}
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Desktop Sidebar */}
        {!isMobile && (
          <aside className="sticky top-14 h-[calc(100vh-3.5rem)] w-56 border-r bg-card">
            {navContent}
          </aside>
        )}

        {/* Mobile Sidebar Sheet */}
        {isMobile && (
          <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
            <SheetContent side="left" className="w-64 p-0 bg-card">
              <SheetTitle className="sr-only">Account Manager Navigation</SheetTitle>
              <div className="flex items-center gap-2 h-14 px-4 border-b">
                <Briefcase className="h-5 w-5 text-[#D4A853]" />
                <span className="font-semibold text-foreground text-sm">AM Portal</span>
              </div>
              {navContent}
            </SheetContent>
          </Sheet>
        )}

        {/* Main Content */}
        <main className={`flex-1 ${isMobile ? "p-4" : "p-6"} overflow-x-hidden`}>{children}</main>
      </div>
    </div>
  );
}

export default AMLayout;
